import { DocumentationWithExternalLink } from '../../../../Definitions/Definition/Drawer/Content/DocumentationWithExternalLink';
import { UiBlockType } from '../../../types';

export function GenerateFileBlockDocumentation() {
  return (
    <DocumentationWithExternalLink
      blockType={UiBlockType.GenerateFile}
      documentationMarkdown={GENERATE_FILE_DOCUMENTATION_MARKDOWN}
    />
  );
}

const GENERATE_FILE_DOCUMENTATION_MARKDOWN = `
# Documentation

## What does this block do?

The Generate File Block takes data from your flow and renders it into a file. The output is a generated file (like a PDF).

The output of the block will include a URL to the document that you can click to easily view the contents.

If the configuration is incorrect—such as an invalid UUID for the template—the block will display an error.

## When to use it?

When you have data in the flow that you want to insert on a pre-designed document.

For generating finalized versions of documents, such as Notices of Admissions (NOA), to be sent via fax or other means.

To automate the creation of documents that require dynamic data, reducing manual effort.

## Example flows

- **Flow Example 1:** A workflow that gathers patient admission details and uses the Generate Document Block to produce a Notice of Admissions (NOA) document, which is then sent via an eFax block.
- **Flow Example 2:** A workflow that collects data from various upstream blocks (e.g., patient info, appointment details) and renders a customized summary report via a Document Template.
- **Flow Example 3:** A workflow that generates or acquires multiple PDF documents that it wishes to combine into a single packet for faxing or uploading to an external system.

## How to configure this block

### Document Template

If you haven't already created your Document Template, do so in Flow Studio at:
[https://app.notablehealth.com/staff/app#/previsit-form-templates](https://app.notablehealth.com/staff/app#/previsit-form-templates)

In the "Document Template" section, use the dropdown search field to locate and select the Document Template you created.

If the selected template has an invalid UUID (e.g., "Invalid uuid" is displayed), correct the template reference to ensure proper document rendering.

**Data Sources**

For each "Data Source" defined in your Document Template, there will be corresponding fields in the block configuration.

Fill out only those fields that correspond to the data elements you intend to render onto the template.

**Mock output for test run**

Enable the "Mock output for test run" toggle if you want to simulate the document generation process without executing a live run.

When enabled, the block will skip document rendering and return preset output data for testing purposes.

**How to test?**

1. Create a Document Template in Flow Studio at:
   [https://app.notablehealth.com/staff/app#/previsit-form-templates](https://app.notablehealth.com/staff/app#/previsit-form-templates)
2. Add your "Text Overlays", specifying the "Data Source" and rendering options.
3. Configure the Generate Document block by selecting your Document Template from the dropdown.
4. Fill in the fields for each "Data Source" used in the Document Template.
5. Optionally, enable the "Mock output for test run" toggle to test the block without initiating a live document generation.
6. Run the flow and click into the document URL in the block output to ensure that the rendered document contains the correct data.
7. Check for any error messages (such as "Invalid uuid") and adjust the configuration accordingly.

**Best practices**

- Always ensure that the selected Document Template has a valid UUID.
- Fill out only the Data Source fields that are required by the Template to avoid unnecessary errors.
- Use the mock test mode to verify configurations and output format before running live data.
- Document how each field in the block configuration maps to the Document Template, so troubleshooting and future audits are easier.

**Example output**

~~~json
{
  "renderResponse": {
    "document": {
      "id": "b746b438-071a-4018-b7ae-214740e51205",
      "gsPath": "documents/b8355cb8-1933-4d7d-9e97-8dd0ea285af1_1473859665716.pdf",
      "created": "2024-12-21T05:14:06.751Z",
      "deleted": null,
      "updated": "2024-12-21T05:14:06.751Z",
      "documentTemplateId": "185de6bb-2898-4d78-931c-5e505ab81a84"
    }
  }
}
~~~

### Markdown

Pass in a markdown string to be rendered into a PDF document.

If your markdown is simple and static, you can declare it directly in the
"Markdown" field.

You can use data from upstream blocks by using the {{myBlock.data}} syntax. For
example, if you have a block with ID "myTextBlock" that outputs text data, you
can reference it in the markdown like this:

~~~markdown
# My Document
This document was generated using data from the flow.

Here is some dynamic content:
{{myTextBlock.data}}
~~~

For more complex markdown, you can construct a markdown string in an upstream
"Script" block and pass it to the "Markdown" field of this block.

**Limitations and considerations**

- The markdown rendering supports basic markdown syntax and a small number of
advanced features like tables.
  - NOTE: tables can render strangely across page breaks. Use with caution.
- Currently, we do not support embedded images or advanced HTML/CSS styling.
- Because markdown is primarily designed for web content, it does not have a
concept of pages like documents. To support forced page breaks, we treat
thematic breaks (---) as page breaks in the generated PDF.

**Best practices**

- Always test your markdown generation to ensure you are using supported
markdown features. Many flavors of markdown exist, and not all are fully
supported, and our support changes over time as we add new capabilities.
- Because the templating within block fields is limited, only use very simple
references to upstream block data when declaring inline.

**Example output**

~~~json
{
  "fileReference": "fileReferenceStringHere"
}
~~~

### HTML

Pass in an HTML string to be rendered into a PDF document.

If your HTML is simple and static, you can declare it directly in the "HTML"
field.

You can use data from upstream blocks by using the {{myBlock.data}} syntax. For
example, if you have a block with ID "myTextBlock" that outputs text data, you
can reference it in the HTML like this:

~~~html
<h1>My Document</h1>
<p>This document was generated using data from the flow.</p>
<p>{{myTextBlock.data}}</p>
~~~

For more complex HTML, construct the HTML string in an upstream "Script" block
and pass it to the "HTML" field of this block.

**Limitations and considerations**

- **No JavaScript.** \`<script>\` tags and inline event handlers (like
  \`onclick\`) are ignored, so your HTML must be fully static.
- **Style with inline \`style\` attributes only.** \`<style>\` blocks and external
  stylesheets are removed, so apply CSS directly on each element, e.g.
  \`<p style="font-family: sans-serif; color: #333">…</p>\`. Only formatting
  properties are kept; anything that references a URL is dropped.
- **Nothing is fetched over the network.** Images, stylesheets, and fonts
  referenced by a URL (\`http\`, \`https\`, etc.) won't load. Embed images
  directly as \`data:\` URLs, e.g.
  \`<img src="data:image/png;base64,<encoded-image>" />\`.
- **Keep it under ~1 MB.** This is the total size of your HTML, including any
  embedded base64 images, so use small, optimized images.
- **Fonts.** Only the three bundled fonts are available (all free for commercial
  use): **Noto Serif**, **Noto Sans**, and **DejaVu Sans Mono**. Text with no
  \`font-family\` renders in the default serif (Noto Serif); set
  \`font-family: sans-serif\` for Noto Sans or \`monospace\` for DejaVu Sans Mono
  via an inline \`style\`. Custom fonts are not supported.
- The rendered output is a single PDF. Use inline page-break styles
  (e.g. \`style="page-break-before: always"\`) to control pagination.

**Best practices**

- Test your HTML in isolation to verify it renders as expected with JavaScript
  disabled and no remote resources.
- Keep templating expressions simple when declaring HTML inline; build complex
  documents in an upstream Script block.

**Example output**

~~~json
{
  "fileReference": "fileReferenceStringHere"
}
~~~

### Base64 to File

Decode a Base64-encoded payload and store it as a file. The block detects the
file type from the decoded bytes, uploads the file, and returns a
\`flowDocument:{id}\` file reference that downstream blocks can consume.

Use the **Base64 Payload** field to provide the encoded string. The field
supports template syntax, so you can pipe a Base64 string from an upstream
block — for example: \`{{myBlock.data.base64String}}\`.

**Output file type**

This mode does **not** force a specific output format. The stored file's
extension is detected from the decoded bytes themselves, so the output
mirrors whatever the upstream block actually produced (e.g., \`.pdf\`,
\`.tif\`, etc.).

If the upstream is fetching a file from an external system, be aware that
content-negotiation headers (such as \`Accept: application/pdf\`) are not
always honored by the source. Many systems return the document in its
originally stored format regardless of what you request.

**If you specifically need a different format** (commonly PDF), do one of
the following:

1. Configure the upstream block to request the desired format directly, if
   the source system supports it.
2. Chain a downstream block that performs the conversion — for example,
   **Transform File → Convert File → PDF**, which handles common image
   formats and multi-page documents.

   When chaining, remember this block returns an *object*, not a bare
   string — reference the file as
   \`{{myGenerateFileBlock.data.fileReference}}\`, not
   \`{{myGenerateFileBlock.data}}\`.

**Limitations and considerations**

- Maximum decoded payload size: 50 MB.
- The decoded bytes must be a recognized file type — unknown or disallowed
  types are rejected.
- Invalid or malformed Base64 strings will fail with a non-retryable error.
- The file extension follows the bytes, not any upstream \`Accept\` header.

**Example output**

~~~json
{
  "fileReference": "flowDocument:abc123"
}
~~~
`;
